'use client';

import { type ParsedUrlQuery, stringify } from 'querystring';
import type { Map } from 'mapbox-gl';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { useExperiments } from '@alltrails/experiments';
import { usePOITrailResults } from '@alltrails/explore-service/hooks/usePOITrailResults';
import { updateBoundingBox, updateMapRotation, updateSkipQuery } from '@alltrails/explore-service/redux/exploreServiceSlice';
import type BoundingBox from '@alltrails/explore-service/types/BoundingBox';
import type ExploreEventData from '@alltrails/explore-service/types/ExploreEventData';
import getMapBoundingBox from '@alltrails/explore-service/utils/getMapBoundingBox';
import getMapRotation from '@alltrails/explore-service/utils/getMapRotation';
import {
  bottomRightLatParamKey,
  bottomRightLngParamKey,
  createDistanceAwayParamString,
  distanceAwayParamKey,
  topLeftLatParamKey,
  topLeftLngParamKey
} from '@alltrails/filters/utils/exploreSearchParams';
import useMap from '@alltrails/maps/hooks/useMap';
import { updatePoiResults, updateTrailResults } from '@alltrails/maps/redux/reducer';
import type { AllTrailsBounds } from '@alltrails/maps/types/Geo';
import type { POISearchResult, TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import useExploreSearchParams, { type ExploreParamCategories } from '@/hooks/useExploreSearchParams';
import { useResetState } from '@/hooks/useResetState';
import { useReturnToExploreRoot } from '@/hooks/useReturnToExploreRoot';
import { explorePath } from '@/utils/constants';
import { useLogExplorePOIViewViewed } from '@/utils/poiAnalyticsHelpers';
import useStoreReturningBounds from '@/hooks/useStoreReturningBounds';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setInitialMapBounds } from '@/utils/slices/exploreSlice';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

// Distance away is not included here because we kick the user back to Explore Root immediately when distance away is applied,
// so adding it to search params and relying on that value would cause a race condition - instead, the distance away param
// is applied directly to the url when we call router.push instead.
// Similarly, bounding box info is also not included because clicking back from the POI sidebar returns the user to the bounds
// they were at when they entered the POI focus state, so there is no point including bounds in the search params.
const paramsToInclude = ['filters', 'sort'] as ExploreParamCategories[];

type ExplorePOIProps = { initialBounds: BoundingBox; initialPOI: POISearchResult; searchParams: ParsedUrlQuery; slug: string };

const ExplorePOI = ({ initialBounds, initialPOI, searchParams }: ExplorePOIProps) => {
  const map = useMap();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const experiments = useExperiments();
  const debugEnabled = experiments['mugen-explore-debug']?.value === 'on';
  usePOITrailResults(initialPOI);
  const { distanceAway, searchResults } = useAppSelector(state => ({
    distanceAway: state.exploreService.distanceAway,
    searchResults: state.exploreService.searchResults
  }));
  const { initialFilters, initialSeasonalMonth, initialSort, initialTrailEngagement } = useExploreSearchParams(paramsToInclude);
  const returnToExploreRoot = useReturnToExploreRoot(true /* persistMapLocation */);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const didLoginRef = useRef(false);

  const resetState = useMemo<Parameters<typeof useResetState>[0]>(
    () => ({
      persistKeys: {
        conditions: ['activeCondition', 'preloadedOverlayUTCs', 'selectedUTC'],
        explore: ['initialMapBounds', 'initialMapCenter'],
        exploreService: ['boundingBox', 'mapRotation']
      },
      initialState: {
        explore: { exploreViewType: 'map poi focus', showFilterBar: true },
        explorePOI: { poi: initialPOI },
        exploreService: {
          filters: initialFilters,
          seasonalMonth: initialSeasonalMonth,
          skipQuery: true, // skip initial query to prevent flickering results when transitioning to/from other explore routes. This should be set to false on the first moveEnd event.
          sort: initialSort,
          trailEngagement: initialTrailEngagement
        },
        maps: { poiResults: {} }
      }
    }),
    // Only reset the store on initial render. `initialPOI` prop is a non-memoized object coming from server, so avoid putting it in the dependency array.
    [initialFilters, initialSeasonalMonth, initialSort, initialTrailEngagement]
  );
  useResetState(resetState);

  useLogExplorePOIViewViewed(initialPOI);

  const onSignUpClose = useCallback(() => {
    if (!didLoginRef.current) {
      returnToExploreRoot();
    }
  }, [returnToExploreRoot]);

  useEffect(() => {
    const trailResults: TrailSearchResult[] = [];
    const poiResults: POISearchResult[] = [];
    searchResults?.forEach(result => {
      if (result.type === 'trail') {
        trailResults.push(result);
      } else if (result.type === 'poi') {
        poiResults.push(result);
      }
    });

    dispatch(updateTrailResults(trailResults ?? []));
    dispatch(updatePoiResults(poiResults ?? []));
  }, [dispatch, searchResults]);

  useStoreReturningBounds();

  useEffect(() => {
    const eventData: ExploreEventData = { boundingBox: initialBounds, mapRotation: 0 };
    map?.fitBounds(
      [
        [initialBounds.bottomLeft.lng, initialBounds.bottomLeft.lat],
        [initialBounds.topRight.lng, initialBounds.topRight.lat]
      ],
      {},
      eventData
    );

    const atBounds: AllTrailsBounds = {
      latitudeBottomRight: initialBounds.bottomRight.lat,
      longitudeBottomRight: initialBounds.bottomRight.lng,
      latitudeTopLeft: initialBounds.topLeft.lat,
      longitudeTopLeft: initialBounds.topLeft.lng
    };
    dispatch(setInitialMapBounds(atBounds));
    // Only run on initial render. `intialBounds` prop is a non-memoized object coming from server, so avoid putting it in the dependency array.
  }, [dispatch, map]);

  useEffect(() => {
    const onMoveEnd = (event: { target: Map } & Partial<ExploreEventData>) => {
      if (debugEnabled) {
        console.log('map zoom: ', map?.getZoom());
      }

      const boundingBox = getMapBoundingBox(event.target);
      const mapRotation = getMapRotation(event.target);
      dispatch(updateBoundingBox(boundingBox));
      dispatch(updateMapRotation(mapRotation));
      dispatch(updateSkipQuery(false));
    };

    map?.on('moveend', onMoveEnd);
    return () => {
      map?.off('moveend', onMoveEnd);
    };
  }, [debugEnabled, dispatch, map]);

  useEffect(() => {
    const showSignUpModal = () => {
      if (!user) {
        setIsSignUpModalOpen(true);
      }
    };
    map?.once('moveend', showSignUpModal);
  }, [map, user]);

  useEffect(() => {
    if (map && distanceAway) {
      const newSearchParams = new URLSearchParams(stringify(searchParams));
      const boundingBox = getMapBoundingBox(map);
      newSearchParams.append(bottomRightLatParamKey, String(boundingBox.bottomRight.lat));
      newSearchParams.append(bottomRightLngParamKey, String(boundingBox.bottomRight.lng));
      newSearchParams.append(topLeftLatParamKey, String(boundingBox.topLeft.lat));
      newSearchParams.append(topLeftLngParamKey, String(boundingBox.topLeft.lng));
      newSearchParams.append(distanceAwayParamKey, createDistanceAwayParamString(distanceAway));
      router.push(`${wrapUrlSafe(explorePath, languageRegionCode)}?${decodeURIComponent(newSearchParams.toString())}`);
    }
  }, [distanceAway, languageRegionCode, map, router, searchParams]);

  return (
    <>
      {isSignUpModalOpen && (
        <SignUpModal
          closeModal={onSignUpClose}
          isOpen
          onSuccess={() => {
            didLoginRef.current = true;
            setIsSignUpModalOpen(false);
          }}
          trigger="POI focus state"
          upsellTrigger="POI focus state"
        />
      )}
    </>
  );
};

export default ExplorePOI;
